import { clearLocalStorageKeysAfterLogout } from "./storage.util";
import { showErrorToast, showSuccessToast } from "./toast";
import { logout } from "services/auth.service";
import { clearAllTransmissionTableSortingKeys } from "./transmissionTable";
import {
  TOAST_AUTO_HIDE_TIME,
  SESSION_EXPIRY_BROADCAST_KEY,
  SESSION_BLOCKED_CODES,
} from "static/constants";

let navigateFunction = null;
let isLoggingOut = false;
let sessionExpiryHandled = false;
let sessionExpiryTimeoutId = null;
let skipPreviousPathRestore = false;

export const setNavigateFunction = (navigate) => {
  navigateFunction = navigate;
};

export const handleLogout = async (isForcedLogout = false) => {
  if (isForcedLogout && !checkIsAuthenticated()) {
    return;
  }

  // Prevent multiple simultaneous logout calls
  if (isLoggingOut) {
    return;
  }

  isLoggingOut = true;

  let redirectUrl = null;

  if (isForcedLogout) {
    redirectToLogin(isForcedLogout, redirectUrl);
    return;
  }

  try {
    const { data, message } = await logout();

    redirectUrl = data?.redirectUrl;
    showSuccessToast(message);
  } catch (error) {
    showErrorToast(error?.message || "Logout failed");
  } finally {
    redirectToLogin(isForcedLogout, redirectUrl);
  }
};

export const redirectToLogin = (isForcedLogout = false, redirectUrl = null) => {
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");

  if (isForcedLogout) {
    const previousPath = localStorage.getItem("previousPath");

    // A deactivated or locked user goes to the default landing page on the next
    // login, not back to the page they were on.
    if (previousPath && userId && userRole && !skipPreviousPathRestore) {
      localStorage.setItem("previousUserId", userId);
      localStorage.setItem("previousUserRole", userRole);
    } else {
      localStorage.removeItem("previousPath");
    }
  } else {
    localStorage.removeItem("previousPath");
    clearAllTransmissionTableSortingKeys();
  }

  clearLocalStorageKeysAfterLogout();

  // Reset the logout flags before redirecting
  isLoggingOut = false;
  skipPreviousPathRestore = false;

  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }

  if (isForcedLogout) {
    window.location.href = "/login";
    return;
  }

  if (navigateFunction) {
    navigateFunction("/login");
  } else {
    window.location.href = "/login";
  }
};

export const checkIsAuthenticated = () => {
  const token = localStorage.getItem("authToken");
  return Boolean(token);
};

export const triggerSessionExpiry = (
  message = "Your session has expired. Please log in again.",
  { priority = false, silent = false } = {}
) => {
  if (sessionExpiryHandled) {
    if (priority && !silent) {
      showErrorToast(message);
    }
    return;
  }
  sessionExpiryHandled = true;

  const tokenAtTrigger = localStorage.getItem("authToken");

  if (!silent) {
    showErrorToast(message);
    localStorage.setItem(SESSION_EXPIRY_BROADCAST_KEY, String(Date.now()));
  }

  sessionExpiryTimeoutId = setTimeout(
    () => {
      if (localStorage.getItem("authToken") !== tokenAtTrigger) {
        return;
      }
      handleLogout(true);
    },
    silent ? 0 : TOAST_AUTO_HIDE_TIME
  );
};

export const cancelSessionExpiry = () => {
  if (sessionExpiryTimeoutId) {
    clearTimeout(sessionExpiryTimeoutId);
    sessionExpiryTimeoutId = null;
  }
  sessionExpiryHandled = false;
};

// `responseBody` is the parsed 401 body; its `data.code` says why the session ended.
export const handleUnauthorizedError = (
  status,
  url = "",
  responseBody = null
) => {
  if (status !== 401 || url.includes("/login") || url.includes("/logout")) {
    return null;
  }

  if (SESSION_BLOCKED_CODES.includes(responseBody?.data?.code)) {
    skipPreviousPathRestore = true;
  }

  if (checkIsAuthenticated()) {
    triggerSessionExpiry();
  } else {
    handleLogout(true);
  }

  return new Promise(() => {});
};
